import type { LocationStatus } from "../../types";
import type { StateCreator } from "zustand";

import type {
  AdministrationBillingExemptionRequest,
  AdministrationChargeWaiverRequest,
  AdministrationBillingMutation,
  AdministrationDashboard,
  AdministrationOrganizationBilling,
  AdministrationOrganizationChargeDetail,
  AdministrationOrganizationDetail,
  AdministrationOrganizationNetwork,
  AdministrationOrganizationPage,
  AdministrationOrganizationPeople,
  AdministrationOrganizationQuery,
  AdministrationPeopleMutation,
} from "../../api/administrationDashboard";
import type { MobileVersionPolicy } from "../../api/mobileVersionPolicy";
import type {
  AdministrationAuditEvent,
  AdministrationAuditPage,
  AdministrationHealth,
  AdministrationOrganizationActivityQuery,
  AuditSeverity,
} from "../../api/administrationOperations";

export const ADMINISTRATION_ORGANIZATION_REALTIME_SCOPES = [
  "overview",
  "people",
  "network",
  "billing",
  "activity",
] as const;
export type AdministrationOrganizationRealtimeScope =
  (typeof ADMINISTRATION_ORGANIZATION_REALTIME_SCOPES)[number];
export const ADMINISTRATION_ORGANIZATION_PEOPLE_CHANGE_SCOPES = [
  "overview", "people", "activity",
] as const satisfies readonly AdministrationOrganizationRealtimeScope[];
export const ADMINISTRATION_ORGANIZATION_BILLING_CHANGE_SCOPES = [
  "overview", "billing", "activity",
] as const satisfies readonly AdministrationOrganizationRealtimeScope[];
export const isAdministrationOrganizationRealtimeScope = (
  value: unknown,
): value is AdministrationOrganizationRealtimeScope =>
  typeof value === "string" &&
  (ADMINISTRATION_ORGANIZATION_REALTIME_SCOPES as readonly string[]).includes(value);

export type AdministrationAuditQuery = { q: string; severity: string; category: string; dateFrom: string; dateTo: string; limit: number; offset: number; sort: string; direction: string };
export type AdministrationOrganizationBillingQuery = { limit: number; offset: number };
export type AdministrationOrganizationPeopleQuery = { memberLimit: number; memberOffset: number; invitationLimit: number; invitationOffset: number };
export type AdministrationOrganizationNetworkQuery = { locationStatus: "all" | LocationStatus; machineStatus: "all" | "active" | "inactive"; locationLimit: number; locationOffset: number; machineLimit: number; machineOffset: number };

export type DashboardSlice = {
  dashboard: AdministrationDashboard | null; dashboardFetchedAt: number | null; dashboardHydrated: boolean; dashboardLoading: boolean; dashboardError: string;
  loadDashboard: (force?: boolean) => Promise<void>;
};
export type MobilePolicySlice = {
  policies: MobileVersionPolicy[]; policiesHydrated: boolean; policiesLoading: boolean; policiesError: string;
  loadPolicies: (force?: boolean) => Promise<void>; replacePolicy: (policy: MobileVersionPolicy) => void;
};
export type HealthSlice = {
  health: AdministrationHealth | null; healthFetchedAt: number | null; healthHydrated: boolean; healthLoading: boolean; healthError: string;
  loadHealth: (force?: boolean) => Promise<void>;
};
export type AuditSlice = {
  auditEvents: AdministrationAuditEvent[]; auditFetchedAt: number | null; auditNextCursor: string | null; auditTotal: number; auditSeverityTotals: Record<AuditSeverity, number>; auditQuery: AdministrationAuditQuery;
  auditHydrated: boolean; auditLoading: boolean; auditLoadingMore: boolean; auditError: string;
  loadAudit: (query?: Partial<AdministrationAuditQuery>, force?: boolean) => Promise<void>; loadMoreAudit: () => Promise<void>;
};
export type OrganizationsSlice = {
  organizationsPage: AdministrationOrganizationPage | null; organizationDetails: Record<string, AdministrationOrganizationDetail>;
  organizationDetailLoading: Record<string, boolean>; organizationDetailErrors: Record<string, string>;
  organizationsQuery: AdministrationOrganizationQuery; organizationsHydrated: boolean; organizationsLoading: boolean; organizationsError: string;
  loadOrganizations: (query?: Partial<AdministrationOrganizationQuery>, force?: boolean) => Promise<void>; loadOrganizationDetail: (id: string, force?: boolean) => Promise<void>;
};
export type OrganizationPeopleSlice = {
  organizationPeople: Record<string, AdministrationOrganizationPeople>; organizationPeopleQueries: Record<string, { id: string } & AdministrationOrganizationPeopleQuery>;
  organizationPeopleLoading: Record<string, boolean>; organizationPeopleErrors: Record<string, string>; organizationPeopleActionLoading: Record<string, AdministrationPeopleMutation["type"] | null>;
  loadOrganizationPeople: (id: string, query?: Partial<AdministrationOrganizationPeopleQuery>, force?: boolean) => Promise<void>;
  runOrganizationPeopleMutation: (id: string, mutation: AdministrationPeopleMutation) => Promise<void>; refreshOrganizationPeopleScope: (id: string) => Promise<void>;
};
export type OrganizationNetworkSlice = {
  organizationNetwork: Record<string, AdministrationOrganizationNetwork>; organizationNetworkQueries: Record<string, { id: string } & AdministrationOrganizationNetworkQuery>;
  organizationNetworkLoading: Record<string, boolean>; organizationNetworkErrors: Record<string, string>;
  loadOrganizationNetwork: (id: string, query?: Partial<AdministrationOrganizationNetworkQuery>, force?: boolean) => Promise<void>;
};
export type OrganizationActivitySlice = {
  organizationActivity: Record<string, AdministrationAuditPage>; organizationActivityQueries: Record<string, { id: string } & AdministrationOrganizationActivityQuery>;
  organizationActivityLoading: Record<string, boolean>; organizationActivityLoadingMore: Record<string, boolean>; organizationActivityErrors: Record<string, string>;
  loadOrganizationActivity: (id: string, query?: Partial<AdministrationOrganizationActivityQuery>, force?: boolean) => Promise<void>;
  loadMoreOrganizationActivity: (id: string, query: AdministrationOrganizationActivityQuery) => Promise<void>; refreshOrganizationActivityScope: (id: string) => Promise<void>;
};
export type OrganizationBillingSlice = {
  organizationBilling: Record<string, AdministrationOrganizationBilling>; organizationBillingQueries: Record<string, { id: string } & AdministrationOrganizationBillingQuery>;
  organizationBillingLoading: Record<string, boolean>; organizationBillingErrors: Record<string, string>; organizationCharges: Record<string, AdministrationOrganizationChargeDetail>;
  organizationChargeLoading: Record<string, boolean>; organizationChargeErrors: Record<string, string>; organizationChargeScopes: Record<string, { organizationId: string; chargeId: string }>;
  organizationBillingActionLoading: Record<string, AdministrationBillingMutation | "exemption" | "waive" | null>;
  loadOrganizationBilling: (id: string, query?: Partial<AdministrationOrganizationBillingQuery>, force?: boolean) => Promise<void>;
  loadOrganizationCharge: (organizationId: string, chargeId: string, force?: boolean) => Promise<void>;
  runOrganizationBillingAction: (organizationId: string, action: AdministrationBillingMutation) => Promise<void>;
  runOrganizationBillingExemption: (organizationId: string, request: AdministrationBillingExemptionRequest) => Promise<void>;
  runOrganizationChargeWaiver: (organizationId: string, chargeId: string, request: AdministrationChargeWaiverRequest) => Promise<boolean>;
  refreshOrganizationBillingScope: (organizationId: string) => Promise<void>;
};

export type AdministrationState = DashboardSlice & MobilePolicySlice & HealthSlice & AuditSlice & OrganizationsSlice & OrganizationPeopleSlice & OrganizationNetworkSlice & OrganizationActivitySlice & OrganizationBillingSlice & {
  refreshLoaded: () => Promise<void>;
  refreshOrganizationScopes: (organizationId: string, scopes: Iterable<AdministrationOrganizationRealtimeScope>) => Promise<void>;
  reset: () => void;
};
export type AdministrationSlice<T> = StateCreator<AdministrationState, [], [], T>;

export const errorMessage = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback;
